/**
 * Is this URL OverlayQA's own app rather than a customer's page?
 *
 * ONE predicate for three surfaces, so they cannot drift: the dashboard's
 * Capture Issues resolver asks it before minting a review (lib/captureIssuesUrl.ts),
 * the server's live-review create route refuses on it (routes/liveReview.ts),
 * and the project routes refuse it as a project's primary domain
 * (lib/ownOrigins.ts wires the server's real origins in).
 *
 * Incident 2026-09-10 (Emily, bonkers.ie): the extension's automatic domain
 * stamp wrote `app.overlayqa.com` onto a project, Capture Issues opened a live
 * review of https://app.overlayqa.com, the proxy framed our own dashboard,
 * Clerk could not load inside the frame, the dashboard's auto-retry reloaded
 * the frame onto the API root, and the API root sends X-Frame-Options: DENY,
 * so the reviewer saw Chrome's "refused to connect". Two external users'
 * projects carried the same domain, and their project tiles showed our
 * sign-in page. Nothing on any surface had ever refused our own host.
 *
 * The sandbox demo (`/sandbox` on the API origin) is a page we own ON
 * PURPOSE and is never refused: it is the onboarding chip and every
 * harness's default target.
 */
/**
 * The production origins, for callers that have no runtime config (the
 * dashboard's pure resolvers) and as the floor every server instance refuses
 * whatever its own env says: a dev-configured API (localhost origins) is still
 * ours, and the production dashboard is still ours to it (mini A/B 2026-09-10:
 * the fixed dev API answered 201 to a review of app.overlayqa.com because its
 * "own" dashboard was localhost:5175). The proxy host is hardcoded for the
 * same reason prod-smoke hardcodes it: read from PROXY_ORIGIN it would vanish
 * the moment the variable is unset.
 */
export const PROD_OWN_APP_ORIGINS = {
    dashboardOrigin: 'https://app.overlayqa.com',
    apiOrigin: 'https://api.overlayqa.com',
    proxyOrigin: 'https://overlayqa-api-production.up.railway.app',
};
/**
 * The sandbox demo page. Same string as the server's SANDBOX_DEMO_PAGE_URL
 * (packages/server/src/seed/sandboxDemoTemplate.ts); the dashboard needs it
 * without importing server code.
 */
export const SANDBOX_DEMO_PAGE_URL = 'https://api.overlayqa.com/sandbox/';
/** Copy for a refused own-app review target. Shared by the server route and the dashboard modal. */
export const URL_OWN_APP_REVIEW_ERROR = 'This address is the OverlayQA app itself, not a page to review. Enter the address of the site you want to review.';
/** Copy for a refused own-app primary domain (POST/PATCH /projects). */
export const PRIMARY_DOMAIN_OWN_APP_ERROR = 'That address is the OverlayQA app itself. Enter the domain of the site this project tracks.';
function parseUrl(value) {
    if (!value)
        return null;
    try {
        return new URL(value);
    }
    catch {
        return null;
    }
}
function parseOrigin(value) {
    return parseUrl(value)?.origin ?? null;
}
/** True for the sandbox demo and any page under it, on the given API origin. */
export function isSandboxDemoUrl(url, origins = PROD_OWN_APP_ORIGINS) {
    const target = parseUrl(url);
    const api = parseOrigin(origins.apiOrigin);
    if (!target || !api || target.origin !== api)
        return false;
    return target.pathname === '/sandbox' || target.pathname.startsWith('/sandbox/');
}
/**
 * Why `url` is our own app, or null when it is a page a customer could review.
 * Parse failures are null: this predicate REFUSES, so it must never refuse by accident.
 */
export function classifyOwnAppUrl(url, origins = PROD_OWN_APP_ORIGINS) {
    const target = parseUrl(url);
    if (!target)
        return null;
    const dashboard = parseOrigin(origins.dashboardOrigin);
    const api = parseOrigin(origins.apiOrigin);
    const proxy = parseOrigin(origins.proxyOrigin);
    if (dashboard && target.origin === dashboard)
        return 'dashboard';
    if (api && target.origin === api)
        return isSandboxDemoUrl(url, origins) ? null : 'api';
    // PROXY_ORIGIN unset means the proxy IS the API origin, already judged above.
    if (proxy && proxy !== api && target.origin === proxy)
        return 'proxy';
    return null;
}
/**
 * True when a bare hostname (a project's primary domain) is our dashboard or
 * our review proxy. Never the API host: the sandbox demo lives there and the
 * onboarding chip stores it as a project's domain on purpose.
 */
export function isOwnAppHostname(hostname, origins = PROD_OWN_APP_ORIGINS) {
    const host = hostname.trim().toLowerCase();
    if (!host)
        return false;
    const matches = (candidate) => !!candidate && (host === candidate.hostname.toLowerCase() || host === candidate.host.toLowerCase());
    if (matches(parseUrl(origins.dashboardOrigin)))
        return true;
    const proxy = parseUrl(origins.proxyOrigin);
    const api = parseOrigin(origins.apiOrigin);
    return !!proxy && proxy.origin !== api && matches(proxy);
}
//# sourceMappingURL=ownAppUrl.js.map